import React, { useState, useEffect } from 'react';
import { 
  Shield, Key, Smartphone, Lock, AlertTriangle, CheckCircle, 
  Copy, RefreshCw, Eye, EyeOff, Download, Clock, User, LogIn, 
  LogOut, Settings, Mail, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';

const SecuritySettings = () => {
  const [loading, setLoading] = useState(true);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [activeTab, setActiveTab] = useState('2fa');
  
  // 2FA Setup state
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [settingUp, setSettingUp] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // 2FA Disable state
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);
  
  // Regenerate backup codes
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);

  useEffect(() => {
    fetchTwoFactorStatus();
  }, []);

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchAuditLogs();
    } else if (activeTab === 'security') {
      fetchSecurityEvents();
      fetchFailedLogins();
    }
  }, [activeTab]);

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await api.get('/auth/2fa/status');
      setTwoFactorStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await api.get('/audit-logs/recent?limit=50');
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchSecurityEvents = async () => {
    try {
      const response = await api.get('/audit-logs/security?limit=20');
      setSecurityEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch security events:', error);
    }
  };

  const fetchFailedLogins = async () => {
    try {
      const response = await api.get('/audit-logs/failed-logins?hours=72');
      setFailedLogins(response.data);
    } catch (error) {
      console.error('Failed to fetch failed logins:', error);
    }
  };

  const handleStartSetup = async () => {
    setSettingUp(true);
    try {
      const response = await api.post('/auth/2fa/setup');
      setSetupData(response.data);
      setShowSetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setSettingUp(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      await api.post('/auth/2fa/verify-setup', { code: verifyCode });
      toast.success('Two-factor authentication enabled!');
      setShowSetup(false);
      setShowBackupCodes(true);
      fetchTwoFactorStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setDisabling(true);
    try {
      await api.post('/auth/2fa/disable', { code: disableCode });
      toast.success('Two-factor authentication disabled');
      setShowDisable(false);
      setDisableCode('');
      fetchTwoFactorStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regenerateCode || regenerateCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      const response = await api.post('/auth/2fa/regenerate-backup-codes', { code: regenerateCode });
      setNewBackupCodes(response.data.backup_codes);
      toast.success('New backup codes generated');
      fetchTwoFactorStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'login_success': return <LogIn className="text-emerald-500" size={16} />;
      case 'login_failed': return <AlertTriangle className="text-red-500" size={16} />;
      case 'logout': return <LogOut className="text-slate-500" size={16} />;
      case '2fa_enabled': case '2fa_disabled': return <Smartphone className="text-indigo-500" size={16} />;
      case 'password_changed': return <Key className="text-amber-500" size={16} />;
      case 'user_created': case 'user_deleted': return <User className="text-blue-500" size={16} />;
      case 'settings_changed': return <Settings className="text-purple-500" size={16} />;
      case 'newsletter_sent': return <Mail className="text-emerald-500" size={16} />;
      case 'user_impersonated': return <Eye className="text-red-500" size={16} />;
      default: return <Clock className="text-slate-400" size={16} />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="flex">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Shield className="text-indigo-600" size={32} />
              Security Settings
            </h1>
            <p className="text-slate-500 mt-2">
              Manage two-factor authentication and view security activity
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="2fa" className="gap-2">
                <Smartphone size={16} />
                Two-Factor Auth
              </TabsTrigger>
              <TabsTrigger value="activity" className="gap-2">
                <Clock size={16} />
                Activity Log
              </TabsTrigger>
              <TabsTrigger value="security" className="gap-2">
                <AlertTriangle size={16} />
                Security Events
              </TabsTrigger>
            </TabsList>

            {/* 2FA Tab */}
            <TabsContent value="2fa">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Lock size={20} />
                      Two-Factor Authentication (2FA)
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">
                      Add an extra layer of security to your account using an authenticator app
                    </p>
                  </div>
                  
                  {loading ? (
                    <div className="text-slate-400">Loading...</div>
                  ) : twoFactorStatus.enabled ? (
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                        <CheckCircle size={16} />
                        Enabled
                      </span>
                    </div>
                  ) : (
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                      Not Enabled
                    </span>
                  )}
                </div>

                <div className="mt-6 border-t border-slate-100 pt-6">
                  {twoFactorStatus.enabled ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                        <div>
                          <p className="font-medium text-emerald-800">2FA is Active</p>
                          <p className="text-sm text-emerald-600">
                            Your account is protected with two-factor authentication
                          </p>
                        </div>
                        <CheckCircle className="text-emerald-500" size={24} />
                      </div>

                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">Backup Codes</p>
                          <p className="text-sm text-slate-500">
                            {twoFactorStatus.backup_codes_remaining} codes remaining
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowRegenerate(true)}>
                          <RefreshCw size={16} className="mr-1" />
                          Regenerate
                        </Button>
                      </div>

                      <div className="pt-4 border-t border-slate-100">
                        <Button 
                          variant="outline" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setShowDisable(true)}
                        >
                          <Trash2 size={16} className="mr-2" />
                          Disable 2FA
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
                          <div>
                            <p className="font-medium text-amber-800">Recommended for School Accounts</p>
                            <p className="text-sm text-amber-700">
                              Two-factor authentication adds an extra layer of security and is required for many school compliance standards.
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button onClick={handleStartSetup} disabled={settingUp}>
                        <Smartphone size={18} className="mr-2" />
                        {settingUp ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* How 2FA Works */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
                <h3 className="font-semibold mb-4">How Two-Factor Authentication Works</h3>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-indigo-600 font-bold">1</span>
                    </div>
                    <h4 className="font-medium mb-1">Install App</h4>
                    <p className="text-sm text-slate-500">
                      Download Google Authenticator or Authy on your phone
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-indigo-600 font-bold">2</span>
                    </div>
                    <h4 className="font-medium mb-1">Scan QR Code</h4>
                    <p className="text-sm text-slate-500">
                      Use the app to scan the QR code we provide
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-indigo-600 font-bold">3</span>
                    </div>
                    <h4 className="font-medium mb-1">Enter Code</h4>
                    <p className="text-sm text-slate-500">
                      Enter the 6-digit code when you login
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Activity Log Tab */}
            <TabsContent value="activity">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h3 className="font-semibold">Recent Activity</h3>
                  <Button variant="outline" size="sm" onClick={fetchAuditLogs}>
                    <RefreshCw size={16} className="mr-1" />
                    Refresh
                  </Button>
                </div>
                
                {logsLoading ? (
                  <div className="p-12 text-center text-slate-500">Loading activity...</div>
                ) : auditLogs.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">No activity recorded yet</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {auditLogs.map((log, index) => (
                      <div key={log.log_id || index} className="px-6 py-4 hover:bg-slate-50">
                        <div className="flex items-start gap-3">
                          <div className="mt-1">{getEventIcon(log.event_type)}</div>
                          <div className="flex-1">
                            <p className="text-sm">{log.event_description}</p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                              <span>{formatDate(log.created_at)}</span>
                              {log.ip_address && <span>IP: {log.ip_address}</span>}
                              {log.user_email && <span>{log.user_email}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Security Events Tab */}
            <TabsContent value="security">
              {/* Failed Logins */}
              {failedLogins.length > 0 && (
                <div className="bg-white rounded-xl border border-red-200 p-6 mb-6">
                  <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-4">
                    <AlertTriangle size={20} />
                    Failed Login Attempts (Last 72 Hours)
                  </h3>
                  <div className="space-y-3">
                    {failedLogins.slice(0, 10).map((log, index) => (
                      <div key={log.log_id || index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-red-800">{log.user_email || 'Unknown'}</p>
                          <p className="text-xs text-red-600">{log.metadata?.reason || 'Invalid credentials'}</p>
                        </div>
                        <div className="text-right text-xs text-red-600">
                          <p>{formatDate(log.created_at)}</p>
                          {log.ip_address && <p>IP: {log.ip_address}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Security Events */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold">Security Events</h3>
                </div>
                
                {securityEvents.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <Shield size={48} className="mx-auto mb-4 text-slate-300" />
                    <p>No security events recorded</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {securityEvents.map((log, index) => (
                      <div key={log.log_id || index} className="px-6 py-4 hover:bg-slate-50">
                        <div className="flex items-start gap-3">
                          <div className="mt-1">{getEventIcon(log.event_type)}</div>
                          <div className="flex-1">
                            <p className="text-sm">{log.event_description}</p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                              <span>{formatDate(log.created_at)}</span>
                              {log.ip_address && <span>IP: {log.ip_address}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          
          {setupData && (
            <div className="py-4 space-y-4">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-4">
                  Scan this QR code with your authenticator app
                </p>
                <img 
                  src={setupData.qr_code} 
                  alt="2FA QR Code" 
                  className="mx-auto border rounded-lg"
                />
              </div>
              
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Or enter this code manually:</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono flex-1">{setupData.secret}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(setupData.secret)}>
                    <Copy size={14} />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Enter the 6-digit code from your app</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetup(false)}>Cancel</Button>
            <Button onClick={handleVerifySetup}>Verify & Enable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="text-amber-600" />
              Save Your Backup Codes
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> Save these backup codes in a safe place. 
                You can use them to access your account if you lose your phone.
              </p>
            </div>
            
            {setupData?.backup_codes && (
              <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                {setupData.backup_codes.map((code, i) => (
                  <div key={i} className="bg-slate-100 rounded px-3 py-2 text-center">
                    {code}
                  </div>
                ))}
              </div>
            )}
            
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => copyToClipboard(setupData?.backup_codes?.join('\n'))}
            >
              <Copy size={16} className="mr-2" />
              Copy All Codes
            </Button>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowBackupCodes(false)}>I&apos;ve Saved My Codes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisable} onOpenChange={setShowDisable}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Disable Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Disabling 2FA will make your account less secure.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Enter your 2FA code to confirm</Label>
              <Input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisable(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDisable2FA}
              disabled={disabling}
            >
              {disabling ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={showRegenerate} onOpenChange={(open) => {
        setShowRegenerate(open);
        if (!open) {
          setRegenerateCode('');
          setNewBackupCodes(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Backup Codes</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {!newBackupCodes ? (
              <>
                <p className="text-sm text-slate-600">
                  This will invalidate your existing backup codes and generate new ones.
                </p>
                
                <div className="space-y-2">
                  <Label>Enter your 2FA code to confirm</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={regenerateCode}
                    onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-sm text-emerald-800">
                    New backup codes generated! Save them securely.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {newBackupCodes.map((code, i) => (
                    <div key={i} className="bg-slate-100 rounded px-3 py-2 text-center">
                      {code}
                    </div>
                  ))}
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => copyToClipboard(newBackupCodes.join('\n'))}
                >
                  <Copy size={16} className="mr-2" />
                  Copy All Codes
                </Button>
              </>
            )}
          </div>
          
          <DialogFooter>
            {!newBackupCodes ? (
              <>
                <Button variant="outline" onClick={() => setShowRegenerate(false)}>Cancel</Button>
                <Button onClick={handleRegenerateBackupCodes}>Generate New Codes</Button>
              </>
            ) : (
              <Button onClick={() => setShowRegenerate(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecuritySettings;
